import {
  StockOrder,
  EditStockOrderFormValues,
  CreateStockOrderFormValues,
} from 'store/api/stock-orders';
import {
  allSharesBlank,
  rowsForEventIds,
  isDefaultOnlySplit,
  buildEventAllocations,
  getEventSplitInitialValues,
} from 'components/stock-transfer-event-split/config';

/**
 * Owner 2026-10-16: the stock order's LTL event split, on every stock order.
 *  - To a general storage (To: Storage, e.g. "Orlando"): the order's existing
 *    Events select picks the events; each gets a share row. Events picked
 *    with no shares behave as before (no split; the LTL freight waits as a
 *    Needs-attention item in QuickBooks).
 *  - To an event (its storage): that event at 100% is the default; adding
 *    events splits the price like the expense split. The default alone is no
 *    split, so such an order stays exactly as before.
 * Saved shares always win over the destination's event (shared rule).
 */

/** The form's initial split rows for a saved order (never rewritten on open). */
export const getStockOrderEventShares = (data: StockOrder) => {
  if (data.eventAllocations?.length) {
    return getEventSplitInitialValues(data.eventAllocations);
  }
  if (data.storage?.type === 'storage') {
    return rowsForEventIds(
      [],
      (data.events || []).map((event) => event.id)
    );
  }
  return [];
};

/**
 * The create / edit body: the rows become `eventAllocations` when there is a
 * split, or `[]` to clear a saved one; otherwise the body is exactly what it
 * was before the split existed.
 */
export const withStockOrderEventSplit = <
  T extends EditStockOrderFormValues | CreateStockOrderFormValues,
>(
  values: T,
  {
    hadSplit = false,
    defaultEventId = null,
  }: { hadSplit?: boolean; defaultEventId?: number | null } = {}
): T => {
  const body = { ...values };
  delete body.eventShares;
  delete body.eventAllocations;
  const rows = values.eventShares || [];
  const generalStorage = values.recipient === 'storage';
  const shares = generalStorage
    ? allSharesBlank(rows)
      ? []
      : rows
    : !hadSplit && isDefaultOnlySplit(rows, defaultEventId, values.ltlPrice)
      ? []
      : rows;
  if (shares.length || hadSplit) {
    return { ...body, eventAllocations: buildEventAllocations(shares) };
  }
  return body;
};

/** A stock order's default event: the event whose storage it goes to. */
export const stockOrderDefaultEventId = (
  values: Pick<
    CreateStockOrderFormValues,
    'orderTo' | 'recipient' | 'storageId'
  >,
  eventOfStorage: (storageId?: number | null) => number | null
) =>
  values.recipient === 'event' && values.orderTo !== 'terminal'
    ? eventOfStorage(values.storageId)
    : null;
